import { CardPayment } from '@mercadopago/sdk-react';
import { forwardRef, memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { logFrontendError } from '../../services/analytics.service';
import type { IBrickError } from '@mercadopago/sdk-react/esm/bricks/util/types/common';
import { useCurrentUser } from '@beholos/holoscommon';

export interface CardFieldsHandle {
  triggerSubmit: () => void;
}

const CardFields = forwardRef<CardFieldsHandle, {
  transactionAmount: number;
  onReady?: () => void;
  onBrickSubmit: (formData: any) => Promise<void>;
  submitDisabled?: boolean;
}>(function CardFields({ transactionAmount, onReady, onBrickSubmit, submitDisabled }) {

  const { user } = useCurrentUser();
  // Keep a ref so handleError never needs user in its deps (avoids Brick remount on user change)
  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);

  // payer.email lets the Brick prefill/skip its email field; hiding it isn't a supported customization option
  const initialization = useMemo(
    () => ({
      amount: transactionAmount,
      ...(user?.email ? { payer: { email: user.email } } : {}),
    }),
    [transactionAmount, user?.email]
  );

  const customization = useMemo(
    () => ({
      paymentMethods: {
        minInstallments: 1,
        maxInstallments: 1,
      },
      visual: {
        locale: 'es-PE',
        hideFormTitle: true,
        style: {
          customVariables: {
            formPadding: '0px',
          },
        },
        texts: {
          emailSectionTitle: "Ingresa tu correo electrónico",
          cardholderName: {
            label: "Nombre del titular",
            placeholder: "Ingresa el nombre del titular",
          },
          email: {
            label: "Correo electrónico",
            placeholder: "Ingresa tu correo electrónico",
            error: "Correo electrónico inválido",
          },
          cardholderIdentification: {
            label: "Identificación del titular",
            placeholder: "Ingresa la identificación del titular",
            error: "Identificación del titular inválida",
          },
          cardNumber: {
            label: "Número de tarjeta",
            placeholder: "Ingresa el número de tarjeta",
            error: "Número de tarjeta inválido",
          },
          expirationDate: {
            label: "Fecha de expiración",
            placeholder: "MM/AA",
            error: "Fecha de expiración inválida",
          },
          securityCode: {
            label: "Código de seguridad",
            placeholder: "•••",
            error: "Código de seguridad inválido",
          },
        }
      },
    }),
    []
  );

  const handleSubmit = useCallback(async (formData: any) => {
    await onBrickSubmit(formData);
  }, [onBrickSubmit]);

  const handleError = useCallback((error: IBrickError) => {
    console.error('CardPayment Brick error:', error);
    logFrontendError('CardPayment Brick', error, {
      userEmail: userRef.current?.email,
    });
  }, []);

  // TEMP diagnostic for WCS-48918 — proves the Brick container mounts once and prop
  // identities stay stable across unrelated re-renders (e.g. toggling terms checkboxes).
  // Remove once MercadoPago confirms the root cause.
  const renderCount = useRef(0);
  const prevProps = useRef({ initialization, customization, onSubmit: handleSubmit, onReady, onError: handleError });
  renderCount.current += 1;
  useEffect(() => {
    console.log(`[MP-diag] CardPayment container MOUNTED (render #${renderCount.current})`);
    return () => console.log('[MP-diag] CardPayment container UNMOUNTED');
  }, []);
  useEffect(() => {
    const prev = prevProps.current;
    const changed: string[] = [];
    if (prev.initialization !== initialization) changed.push('initialization');
    if (prev.customization !== customization) changed.push('customization');
    if (prev.onSubmit !== handleSubmit) changed.push('onSubmit');
    if (prev.onReady !== onReady) changed.push('onReady');
    if (prev.onError !== handleError) changed.push('onError');
    console.log(`[MP-diag] render #${renderCount.current} — Brick prop references changed:`, changed.length ? changed : 'none');
    prevProps.current = { initialization, customization, onSubmit: handleSubmit, onReady, onError: handleError };
  });

  return (
    <div>
      <style>{`
        #cardPaymentBrick_container button[type="submit"] { margin-top: 30px !important; }
        ${submitDisabled ? '#cardPaymentBrick_container button[type="submit"] { pointer-events: none !important; opacity: 0.5 !important; cursor: not-allowed !important; }' : ''}
      `}</style>
      <CardPayment
        initialization={initialization}
        customization={customization}
        onSubmit={handleSubmit}
        onReady={onReady}
        onError={handleError}
      />
    </div>
  );
});

export default memo(CardFields);
